'use client';

import useGlobalState from '@hooks/useGlobalState';
import MainLayout from '@wconcept/wconcept-ui-renewal/components/layouts/MainLayout';
import SubLayout from '@wconcept/wconcept-ui-renewal/components/layouts/SubLayout';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import {
  NewGnbInfo,
  SearchBar,
  GnbPcInfo,
  GnbPcMegaMenuInfo,
  PcGnbCategoryInfo,
  LargeCategory,
  MiddleCategory,
} from '@wconcept/wconcept-common/models/renewal/gnb';
import {
  LnbDisplayInfo,
  LnbV2Menu,
} from '@wconcept/wconcept-common/models/renewal/lnb';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MainLayoutDesktop from '@wconcept/wconcept-ui-renewal/src/components/layouts/_desktop/MainLayoutDesktop';
import { use, useCallback, useEffect, useState } from 'react';
import {
  requestAutoComplete,
  // requestSearchPopularWords,
} from '@apis/display/search';
import {
  PopularWords,
  Brand,
  SearchInitInfo,
  // KeywordInfo,
} from '@wconcept/wconcept-common/models/display/search';
import {
  getCookieWckRPImage,
  getWckRecentResultList,
  setLastKwd,
  setWckRecentResultList,
} from '@wconcept/wconcept-common/utils/cookie';
import useHistoryRouter from '@hooks/useLinkTo';
import {
  addAsIsMWebDomain,
  addAsIsPcWebDomain,
  getMWebTargetUrl,
} from '@wconcept/wconcept-common/utils/domainUtil';
import {
  duplicateValueCheck,
  sortObject,
} from '@wconcept/wconcept-common/utils/commonUtils';
import { getMediumCategoryName } from '@wconcept/wconcept-common/utils/category';
import useModalManager from '@hooks/useModalManager';
import { encodeSpecialChars } from '@wconcept/wconcept-common/utils/text';
import { set } from 'lodash-es';
import useGa4Event from '@hooks/useGa4Event';
import CustomErrorBoundary from '@components/common/CustomErrorBoundary';
import { SearchKeywordInfo } from '@wconcept/wconcept-common/models/renewal/search';
import { requestSearchInitPopularKeyword } from '@apis/renewal/search';
import { getSearchTypeText } from '@utils/text';

interface IHeaderLayout {
  className?: string; // 0
  pageTitle?: string; // 0
  children?: React.ReactNode;
  contTitsuffix?: React.ReactNode;
  megaMenu?: GnbPcMegaMenuInfo;
  gnbPcInfo?: GnbPcInfo;
  gnbInfo: NewGnbInfo[];
  isTopBtn?: boolean;
  gnbType?: string;
  isShortFormBtnTooltip?: boolean;
  styleClipTooltipPoint?: number;
  searchInitInfo?: SearchKeywordInfo;
  isHistoryBtn?: boolean;
  isStyleClipAddBtn?: boolean;
  setTooltipShown?: (value: string) => void;
  subHeaderType?: 'main' | 'subPage' | 'subDetail' | undefined;
  /** 브레드크럼 영역 */
  breadcrumbNode?: React.ReactNode;
}

function DesktopHeaderLayoutContainer({
  pageTitle,
  children,
  className,
  megaMenu,
  breadcrumbNode,
  gnbPcInfo,
  contTitsuffix,
  gnbInfo,
  searchInitInfo,
  isShortFormBtnTooltip,
  styleClipTooltipPoint,
  isTopBtn,
  gnbType,
  subHeaderType,
  isHistoryBtn,
  isStyleClipAddBtn,
  setTooltipShown,
}: IHeaderLayout) {
  const { datadogProperty, appInfo, assistData, isMobile, pageInfo } =
    useGlobalState();
  const { linkTo } = useHistoryRouter();
  const router = useRouter();
  const pathname = usePathname();
  const {
    sendGa4HeaderClick,
    sendGa4BannerClick,
    sendGa4LnbClick,
    sendGa4ButtonClick,
    sendGa4GnbClick,
    sendGa4KeywordClick,
  } = useGa4Event();

  const [recentSearchKeywords, setRecentSearchKeywords] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const { openLoginConfirm } = useModalManager();

  const [historyImg, setHistoryImg] = useState('');

  const getHistoryImg = useCallback(async () => {
    const imgUrl = await getCookieWckRPImage();
    if (!assistData?.isLogin) {
      setHistoryImg('');
      return;
    }
    setHistoryImg(imgUrl);
  }, [assistData]);

  useEffect(() => {
    getHistoryImg();
  }, [getHistoryImg]);

  useEffect(() => {
    const fetchRecentSearchKeywords = async () => {
      const keywordList = await getWckRecentResultList();
      setRecentSearchKeywords(keywordList);
    };

    fetchRecentSearchKeywords();
  }, []);

  const searchPopularWordInfo = useQuery({
    queryKey: ['searchPopularWords'],
    queryFn: () => requestSearchInitPopularKeyword(),
    enabled: false,
  });

  const searchAutoKeywordInfo = useQuery({
    queryKey: ['searchAutoKeywordInfo', searchKeyword],
    queryFn: () => requestAutoComplete(encodeURIComponent(searchKeyword)),
    enabled: searchKeyword !== '',
  });

  const onClickAutoComplete = useCallback((keyword: string) => {
    setSearchKeyword(keyword);
  }, []);

  // gnb 메뉴 클릭
  const onClickGnbMenu = useCallback(
    (value: NewGnbInfo, i: number) => {
      sendGa4GnbClick({
        area: 'gnb_list_tab',
        listNo: (i + 1).toString(),
        text: value.menuTitle,
        page: 'gnb',
        adYn: value.adYn,
      });
      linkTo.push({
        url: `${value.landingUrl}`,
      });
    },
    [linkTo, sendGa4GnbClick]
  );

  const onClickStyleClipAddBtn = useCallback(() => {
    sendGa4ButtonClick({
      text: '등록하기',
      tabNm: pageTitle === '스타일클립' ? '스타일클립 등록' : pageTitle,
      area: 'styleclip_floating_register_button',
    });
    if (assistData?.isLogin) {
      linkTo.push({
        url: '/rn/styleclip/create',
      });
    } else {
      openLoginConfirm();
    }
  }, [
    assistData?.isLogin,
    linkTo,
    openLoginConfirm,
    pageTitle,
    sendGa4ButtonClick,
  ]);

  const onClickMediumCategory = useCallback(
    async (value: PcGnbCategoryInfo) => {
      if (!value.landingUrl) {
        return;
      }
      linkTo.push({
        url: value.landingUrl,
      });
    },
    [linkTo]
  );

  const onClickLargeCategory = useCallback(
    async (value: PcGnbCategoryInfo, largeCategory: LargeCategory) => {
      const tabName = value.mediumName; // 추가 필요
      sendGa4LnbClick({
        area: 'lnb_category_tab',
        listNo: value.displayOrder.toString(),
        text: largeCategory.categoryName,
        tabNm: tabName,
        page: 'lnb',
      });
      if (!largeCategory.landingUrl) {
        return;
      }
      linkTo.push({
        url: largeCategory.landingUrl,
      });
    },
    [linkTo, sendGa4LnbClick]
  );

  const onClickMiddleCategory = useCallback(
    async (
      value: PcGnbCategoryInfo,
      largeCategory: LargeCategory,
      middleCategory: MiddleCategory
    ) => {
      const tabName = `${value.mediumName}>${largeCategory.categoryName}`; // 추가 필요
      sendGa4LnbClick({
        area: 'lnb_category_tab',
        listNo: (value.displayOrder + 1).toString(),
        page: 'lnb',
        text: middleCategory.categoryName,
        tabNm: tabName,
      });
      if (!middleCategory.landingUrl) {
        return;
      }
      linkTo.push({
        url: middleCategory.landingUrl,
      });
    },
    [linkTo, sendGa4LnbClick]
  );

  const onClickLogo = useCallback(() => {
    sendGa4HeaderClick({ text: 'logo' });
    if (pathname === '/rn/women') {
      window.location.reload();
    } else {
      linkTo.push({
        url: '/rn/women',
      });
    }
  }, [linkTo, pathname, sendGa4HeaderClick]);

  const onClickLogin = useCallback(async () => {
    sendGa4HeaderClick({ text: 'login' });
    const landingUrl = await addAsIsPcWebDomain('/Member/Login');
    linkTo.push({
      url: landingUrl,
    });
  }, [linkTo, sendGa4HeaderClick]);

  const onClickLogout = useCallback(async () => {
    sendGa4HeaderClick({ text: 'logout' });

    const landingUrl = await addAsIsPcWebDomain('/Member/Logout');
    linkTo.push({
      url: landingUrl,
    });
  }, [linkTo, sendGa4HeaderClick]);

  const onClickHeart = useCallback(() => {
    sendGa4HeaderClick({ text: 'myheart' });
    if (assistData.isLogin) {
      if (pathname.includes('/rn/myheart')) {
        window.location.href = '/rn/myheart';
      } else {
        linkTo.push({
          url: `/rn/myheart`,
        });
      }
    } else {
      openLoginConfirm(
        `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/rn/myheart`,
        true
      );
    }
  }, [
    assistData.isLogin,
    linkTo,
    openLoginConfirm,
    pathname,
    sendGa4HeaderClick,
  ]);

  const onClickMyPage = useCallback(async () => {
    sendGa4HeaderClick({ text: 'mypage' });

    const landingUrl = await addAsIsPcWebDomain('/MyPage');
    linkTo.push({
      url: landingUrl,
    });
  }, [linkTo, sendGa4HeaderClick]);

  const onClickCart = useCallback(async () => {
    sendGa4HeaderClick({ text: 'cart' });

    const landingUrl = await addAsIsPcWebDomain('/Basket');
    linkTo.push({
      url: landingUrl,
    });
  }, [linkTo, sendGa4HeaderClick]);

  const onClickJoin = useCallback(() => {}, []);

  const onKeywordClick = useCallback(
    async (
      value: string,
      type: string,
      recentKeywordList: string[],
      no: number
    ) => {
      sendGa4KeywordClick({
        page: 'search',
        area: 'search_page_keyword',
        text: value,
        pageNo: no,
        searchType: getSearchTypeText(type),
      });
      // 최근검색어 저장
      setWckRecentResultList(
        duplicateValueCheck([value, ...recentKeywordList])
      );
      setLastKwd(value);
      const landingUrl = await getMWebTargetUrl(
        '',
        `/search?keyword=${encodeSpecialChars(value)}&type=${type}`
      );
      linkTo.push({
        url: landingUrl,
      });
    },
    [linkTo, sendGa4KeywordClick]
  );

  const onClickLnbMenu = useCallback(
    (lnbMenu: LnbV2Menu, type: string) => {
      const areaTitle = type === 'theme' ? 'lnb_theme_tab' : 'lnb_page_tab';
      sendGa4LnbClick({
        area: areaTitle,
        listNo: lnbMenu.menuSort.toString(),
        text: lnbMenu?.menuTitle,
        page: 'lnb',
      });

      linkTo.push({
        url: lnbMenu.landingUrl,
      });
    },
    [linkTo, sendGa4LnbClick]
  );

  const onClickAutoSearchResult = useCallback(
    async (
      value: string,
      type: string,
      recentKeywordList: string[],
      item?: Brand,
      no?: number
    ) => {
      sendGa4KeywordClick({
        page: 'search',
        area: 'search_page_keyword',
        text: type === 'keyword' ? value : item?.keyword || '',
        pageNo: no,
        searchType: getSearchTypeText(type),
      });
      // 최근검색어 저장
      setWckRecentResultList(
        duplicateValueCheck([
          type === 'keyword' ? value : item?.keyword ?? '',
          ...recentKeywordList,
        ])
      );
      setLastKwd(value);
      let landingUrl;
      if (type === 'keyword') {
        landingUrl = await getMWebTargetUrl(
          '',
          `/search?keyword=${encodeSpecialChars(value)}&type=${type}`
        );
      } else if (type === 'brand') {
        landingUrl = await getMWebTargetUrl('', `/rn/brand/${value}`);
      }

      linkTo.push({
        url: `${landingUrl}`,
      });
    },
    [linkTo, sendGa4KeywordClick]
  );

  const onPressEnter = useCallback(
    async (value: string, recentKeywordList: string[]) => {
      const keyword = value.trim();
      if (keyword === '') {
        window.alert('검색어를 입력하세요.');
        return;
      }
      // 최근검색어 저장
      setWckRecentResultList(
        duplicateValueCheck([value, ...recentKeywordList])
      );
      setLastKwd(value);
      const landingUrl = await getMWebTargetUrl(
        '',
        `/search?keyword=${encodeSpecialChars(value)}&type=direct`
      );
      linkTo.push({
        url: landingUrl,
      });
    },
    [linkTo]
  );

  const onClickHistory = useCallback(async () => {
    if (!assistData?.isLogin) {
      openLoginConfirm(
        `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/rn/history`,
        true
      );
      return;
    }
    linkTo.push({
      url: '/rn/history',
    });
  }, [assistData, linkTo, openLoginConfirm]);

  const onSearchClick = useCallback(
    async (
      e: HTMLInputElement,
      recentKeywordsList: string[],
      searchInitInfos?: SearchKeywordInfo
    ) => {
      const target = e;
      const keyword = target.value.trim();

      if (target.value.length === 0) {
        if (searchInitInfos && searchInitInfos?.targetPageType !== 'NONE') {
          // GA4
          sendGa4BannerClick({
            page: 'search',
            area: 'search_header_banner',
            text: searchInitInfos?.searchTitle,
            bannerType: searchInitInfos?.ga4ClickEvent?.eventType,
            issueCd: searchInitInfos?.ga4ClickEvent?.eventValue,
            bannerForm: 'text',
          });

          if (searchInitInfos?.landingUrl) {
            linkTo.push({
              url: searchInitInfos?.landingUrl,
            });
          }
          return;
        }
        window.alert('검색어를 입력하세요.');
      } else if (keyword === '') {
        // 추천검색어 있을시 넘어가도록
        window.alert('검색어를 입력하세요.');
      } else {
        const landingUrl = await getMWebTargetUrl(
          '',
          `/search?keyword=${encodeSpecialChars(target.value)}&type=direct`
        );
        linkTo.push({
          url: landingUrl,
        });
        setWckRecentResultList(
          duplicateValueCheck([target.value, ...recentKeywordsList])
        );
        setLastKwd(target.value);
      }
    },
    [linkTo]
  );

  // ITEM RENDERER : getHeaderTypeRender
  return (
    <MainLayoutDesktop
      className={className}
      title={pageTitle}
      megaMenu={megaMenu}
      breadcrumbNode={breadcrumbNode}
      gnbPcInfo={gnbPcInfo}
      gnbInfo={gnbInfo}
      basketCount={parseInt(
        assistData?.basketCount ? assistData?.basketCount : '0',
        10
      )}
      historyBtnRs="88"
      isHistoryBtn={isHistoryBtn}
      isStyleClipAddBtn={isStyleClipAddBtn}
      contTitsuffix={contTitsuffix}
      searchPopularWords={searchPopularWordInfo.data}
      searchAutoKeywordInfo={searchAutoKeywordInfo.data}
      recentSearchKeywordsList={recentSearchKeywords}
      searchInitInfo={searchInitInfo || ({} as SearchKeywordInfo)}
      isNativeAppBoth={appInfo?.isNativeAppBoth ?? false}
      isDesktop={!isMobile}
      isLogin={assistData?.isLogin ?? false}
      agentOnBoardingYn={datadogProperty?.agentOnBoardingYn}
      historyImg={historyImg}
      isShortFormBtnTooltip={isShortFormBtnTooltip}
      setTooltipShown={setTooltipShown}
      styleClipTooltipPoint={styleClipTooltipPoint}
      onClickStyleClipAddBtn={onClickStyleClipAddBtn}
      getHistoryImg={getHistoryImg}
      onClickLogo={onClickLogo}
      onClickJoin={onClickJoin}
      onClickLogin={onClickLogin}
      onClickLogout={onClickLogout}
      onClickHeart={onClickHeart}
      onClickMyPage={onClickMyPage}
      onClickCart={onClickCart}
      onClickMediumCategory={onClickMediumCategory}
      onClickLargeCategory={onClickLargeCategory}
      onClickMiddleCategory={onClickMiddleCategory}
      onPressEnter={onPressEnter}
      onClickAutoSearchResult={onClickAutoSearchResult}
      onKeywordClick={onKeywordClick}
      requestSearchPopularWords={() => searchPopularWordInfo?.refetch()}
      requestAutoComplete={onClickAutoComplete}
      onClickGnbMenu={onClickGnbMenu}
      onClickLnbMenu={onClickLnbMenu}
      onSearchClick={onSearchClick}
      onClickHistory={onClickHistory}
      onClickTop={() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }}
    >
      <CustomErrorBoundary>{children}</CustomErrorBoundary>
    </MainLayoutDesktop>
  );
}

export default DesktopHeaderLayoutContainer;
